import React from "react";
import { Paper, Box, Typography, IconButton, Button, LinearProgress } from "@material-ui/core";
import { Star, MoreVert, ArrowForward } from "@material-ui/icons";
import { toast } from "react-toastify";
import { Can } from "../../../components/Can";
import { i18n } from "../../../translate/i18n";
import ChannelIcon from "../../../components/ChannelIcon";
import ConnectionStatusBadge from "./ConnectionStatusBadge";

const getActionButtonText = (whatsApp) => {
  // WABA (Meta) no usa QR: su acción es siempre editar/ver la conexión.
  if (!whatsApp.usesQr) return "Editar";
  if (whatsApp.status === "qrcode") return "Nuevo QR-Code";
  if (whatsApp.status === "CONNECTED") return "Editar";
  return "Nuevo QR-Code";
};

/**
 * Tarjeta individual de una conexión WhatsApp.
 * Consume el objeto `whatsApp` ya normalizado por `useWhatsApps` — nunca lee `provider`
 * directamente para bifurcar lógica. Usa los capability flags: `isDefault`, `status`.
 *
 * @param {object}   whatsApp            — Objeto normalizado (canCleanSession, hasQrCapability, etc.)
 * @param {object}   classes             — makeStyles result de useConnectionsStyles
 * @param {object}   theme               — useTheme() result
 * @param {object}   user                — { profile } para <Can>
 * @param {Function} formatDateTime      — useTimezone hook
 * @param {Function} onMenuOpen         — (event, whatsApp) => void
 * @param {Function} onActionButtonClick — (whatsApp) => void
 */
const ConnectionCard = ({
  whatsApp,
  classes,
  theme,
  user,
  formatDateTime,
  onMenuOpen,
  onActionButtonClick,
  baileysVersion,
  baileysUpdatedAt,
}) => (
  <Paper className={classes.connectionCard}>
    <Box className={classes.cardHeader} display="flex" alignItems="flex-start">
      {whatsApp.isDefault && (
        <Box className={classes.defaultBadge}>
          <Star className={classes.defaultIcon} />
          Predeterminada
        </Box>
      )}
      {/* El logo identifica el canal. Antes era un enlace al SVG de WhatsApp en Wikimedia:
          además de pintar WhatsApp en tarjetas de WABA, Messenger e Instagram, dejaba la
          pantalla sin iconos si el navegador no alcanzaba ese dominio. */}
      <ChannelIcon
        provider={whatsApp.provider}
        size={48}
        className={classes.whatsappIcon}
      />
      <Box className={classes.connectionInfo}>
        <Typography className={classes.connectionName}>
          {whatsApp.name || "Conexión principal"}
        </Typography>
        <Box style={{ marginTop: theme.spacing(1) }}>
          <Box className={classes.connectionDetail}>
            <span className={classes.detailLabel}>Número:</span>
            <span className={classes.detailValue}>
              {whatsApp.number || "-"}
            </span>
          </Box>
          {whatsApp.waName && (
            <Box className={classes.connectionDetail}>
              <span className={classes.detailLabel}>Nombre:</span>
              <span className={classes.detailValue}>{whatsApp.waName}</span>
            </Box>
          )}
          {whatsApp.provider !== "waba" ? (
            <Box className={classes.connectionDetail}>
              <span className={classes.detailLabel}>
                {i18n.t("connections.table.token")}:
              </span>
              <span className={classes.detailValue}>
                {/* Se muestra una vez, al crearla (doc 0431, A-10): aquí solo el final. */}
                {whatsApp.hasToken
                  ? whatsApp.tokenPreview
                  : i18n.t("connections.table.tokenEmpty")}
              </span>
            </Box>
          ) : (
            whatsApp.phoneNumberId && (
              <Box className={classes.connectionDetail}>
                <span className={classes.detailLabel}>Phone ID:</span>
                <span
                  className={classes.detailValue}
                  title={whatsApp.phoneNumberId}
                >
                  {whatsApp.phoneNumberId}
                </span>
              </Box>
            )
          )}
          {whatsApp.updatedAt && (
            <Box className={classes.connectionDetail}>
              <span className={classes.detailLabel}>Fecha:</span>
              <span className={classes.detailValue}>
                {formatDateTime(whatsApp.updatedAt)}
              </span>
            </Box>
          )}
          <Box className={classes.connectionDetail}>
            <span className={classes.detailLabel}>Conexión:</span>
            <span className={classes.detailValue}>
              {whatsApp.providerLabel || "API Baileys"}
            </span>
          </Box>
          {String(whatsApp.providerLabel || "API Baileys")
            .toLowerCase()
            .includes("baileys") &&
            baileysVersion && (
              <Box className={classes.connectionDetail}>
                <span className={classes.detailLabel}>Versión:</span>
                <span
                  style={{
                    flex: 1,
                    fontSize: "0.72rem",
                    color: "#25d366",
                    fontWeight: 600,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  v{baileysVersion}
                  {baileysUpdatedAt ? ` · act. ${baileysUpdatedAt}` : ""}
                </span>
              </Box>
            )}
        </Box>
        <Box style={{ marginTop: theme.spacing(1) }}>
          <ConnectionStatusBadge status={whatsApp.status} classes={classes} />
        </Box>
        {whatsApp.reconnectAttempt > 0 && (
          <Box style={{ marginTop: theme.spacing(1) }}>
            <Typography
              style={{
                fontSize: "0.72rem",
                color: "#ff9800",
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Reintentando conexión ({whatsApp.reconnectAttempt}
              {whatsApp.reconnectMaxAttempts
                ? `/${whatsApp.reconnectMaxAttempts}`
                : ""}
              )…
            </Typography>
            <LinearProgress
              variant={
                whatsApp.reconnectMaxAttempts ? "determinate" : "indeterminate"
              }
              value={
                whatsApp.reconnectMaxAttempts
                  ? Math.min(
                      100,
                      (whatsApp.reconnectAttempt /
                        whatsApp.reconnectMaxAttempts) *
                        100
                    )
                  : undefined
              }
              style={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}
        {whatsApp.provider === "waba" && whatsApp.status === "CONNECTED" && (
          <Box style={{ marginTop: theme.spacing(1) }}>
            {whatsApp.receptionConfirmed ? (
              <span
                style={{ fontSize: "0.72rem", color: "#25D366", fontWeight: 600 }}
              >
                ✓ Recepción confirmada (envía y recibe)
              </span>
            ) : (
              <Box
                style={{
                  fontSize: "0.72rem",
                  color: "#b26a00",
                  background: "#fff8e1",
                  border: "1px solid #ffe0a3",
                  borderRadius: 6,
                  padding: "6px 8px",
                  lineHeight: 1.4,
                }}
              >
                ⚠️ Envío OK. <b>Falta confirmar recepción</b>: envía un WhatsApp de
                prueba a este número desde otro teléfono.
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Can
        role={user.profile}
        perform="connections-page:editOrDeleteConnection"
        yes={() => (
          <IconButton
            className={classes.menuButton}
            onClick={(e) => onMenuOpen(e, whatsApp)}
            size="small"
          >
            <MoreVert />
          </IconButton>
        )}
      />
    </Box>
    <Box className={classes.cardBody}>
      {whatsApp.provider === "waba" && (
        <Button
          size="small"
          fullWidth
          onClick={() => {
            const backendUrl =
              window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const webhookUrl = `${backendUrl}/whatsapp-provider/webhook/waba`;
            if (navigator.clipboard) {
              navigator.clipboard.writeText(webhookUrl).then(
                () =>
                  toast.success(
                    "URL del webhook copiada. Pégala en Meta → Webhooks (en desarrollo, reemplaza el host por tu túnel público)."
                  ),
                () => toast.info(webhookUrl)
              );
            } else {
              toast.info(webhookUrl);
            }
          }}
          style={{
            marginBottom: 8,
            textTransform: "none",
            fontSize: "0.78rem",
            color: "#1877F2",
          }}
        >
          📋 Copiar webhook para Meta
        </Button>
      )}
      <Can
        role={user.profile}
        perform="connections-page:actionButtons"
        yes={() => (
          <Button
            className={classes.actionButton}
            onClick={() => onActionButtonClick(whatsApp)}
            endIcon={<ArrowForward />}
            fullWidth
          >
            {getActionButtonText(whatsApp)}
          </Button>
        )}
      />
    </Box>
  </Paper>
);

export default ConnectionCard;
